import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { Restaurant } from "./types";

type RestaurantStore = {
    // Server-side data (not persisted — re-fetched per session)
    currentRestaurant: Restaurant | null;
    restaurants: Restaurant[];

    // Persisted UI preferences
    sidebarCollapsed: boolean;

    setCurrentRestaurant: (restaurant: Restaurant | null) => void;
    setRestaurants: (restaurants: Restaurant[]) => void;
    addRestaurant: (restaurant: Restaurant) => void;
    updateRestaurant: (id: string, updates: Partial<Restaurant>) => void;
    removeRestaurant: (id: string) => void;
    clearRestaurants: () => void;

    toggleSidebar: () => void;
    setSidebarCollapsed: (collapsed: boolean) => void;
};

export const useRestaurantStore = create<RestaurantStore>()(
    persist(
        (set) => ({
            currentRestaurant: null,
            restaurants: [],
            sidebarCollapsed: false,

            setCurrentRestaurant: (restaurant) => {
                set({ currentRestaurant: restaurant });
            },

            setRestaurants: (restaurants) => {
                set({ restaurants });
            },

            addRestaurant: (restaurant) => {
                set((state) => ({
                    restaurants: [...state.restaurants, restaurant],
                }));
            },

            updateRestaurant: (id, updates) => {
                set((state) => ({
                    restaurants: state.restaurants.map((r) =>
                        r.id === id ? { ...r, ...updates } : r
                    ),
                    currentRestaurant:
                        state.currentRestaurant?.id === id
                            ? { ...state.currentRestaurant, ...updates }
                            : state.currentRestaurant,
                }));
            },

            removeRestaurant: (id) => {
                set((state) => ({
                    restaurants: state.restaurants.filter((r) => r.id !== id),
                    currentRestaurant:
                        state.currentRestaurant?.id === id ? null : state.currentRestaurant,
                }));
            },

            clearRestaurants: () => {
                set({ restaurants: [], currentRestaurant: null });
            },

            toggleSidebar: () =>
                set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
            setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
        }),
        {
            name: "restaurant-hub-restaurant-prefs",
            storage: createJSONStorage(() => localStorage),
            // Server data stays ephemeral — only persist the sidebar layout preference.
            partialize: (state) => ({ sidebarCollapsed: state.sidebarCollapsed }),
        }
    )
);
